import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import client from '@/api/client'
import { aplicarMarca } from '@/composables/useCorDaMarca'
import type { Contexto, Empresa, Unidade } from '@/types'

export const useContextoStore = defineStore('contexto', () => {
  const empresas = ref<Empresa[]>([])
  const empresasInteiras = ref<number[]>([])
  const empresaSelecionada = ref<number | null>(null)
  const unidadeSelecionada = ref<number | null>(null)
  const carregado = ref(false)

  /**
   * Incrementado a cada troca de contexto. As telas observam este número para
   * recarregar seus dados — sem isso, trocar de unidade mudaria o seletor e deixaria a
   * tela mostrando os números da unidade anterior, que é pior do que não trocar.
   */
  const versao = ref(0)

  const unidades = computed<Unidade[]>(() => empresas.value.flatMap((e) => e.unidades))

  const unidadeAtual = computed<Unidade | null>(
    () => unidades.value.find((u) => u.id === unidadeSelecionada.value) ?? null,
  )

  /**
   * O foco tem três estados, e a interface precisa distinguir os três:
   *
   *   consolidado  — nem empresa nem unidade;
   *   empresa      — uma empresa inteira, várias unidades;
   *   unidade      — uma unidade só.
   *
   * O do meio não existia, e a falta dele obrigava quem responde por uma empresa de
   * sete unidades a escolher entre o grupo todo e uma unidade por vez.
   */
  const consolidado = computed(
    () => empresaSelecionada.value === null && unidadeSelecionada.value === null,
  )

  const focoEmEmpresa = computed(
    () => empresaSelecionada.value !== null && unidadeSelecionada.value === null,
  )

  /** A empresa em foco — a escolhida, ou a dona da unidade escolhida. */
  const empresaAtual = computed<Empresa | null>(
    () => empresas.value.find((e) => e.id === empresaSelecionada.value) ?? null,
  )

  /**
   * Cor de acento vigente. No consolidado não existe uma empresa em foco, então
   * a interface volta ao neutro — o que, junto com a régua multicolorida do
   * topo, é o sinal de que se está vendo o grupo inteiro e não uma empresa.
   */
  const corAtiva = computed<string | null>(() => empresaAtual.value?.cor ?? null)

  /**
   * O seletor vale a pena com mais de uma unidade OU mais de uma empresa.
   *
   * A segunda metade não é hipótese: uma empresa com uma unidade só, ao lado de outra
   * com seis, dá sete unidades — mas quem tem acesso só à primeira precisa continuar
   * podendo alternar entre "a minha empresa" e o consolidado.
   */
  const seletorRelevante = computed(
    () => unidades.value.length > 1 || empresas.value.length > 1,
  )

  const rotulo = computed(() => {
    if (unidadeAtual.value) {
      return `${empresaAtual.value?.nome ?? ''} — ${unidadeAtual.value.nome}`
    }
    if (empresaAtual.value) {
      return `${empresaAtual.value.nome} — todas as unidades`
    }
    return 'Todas as minhas unidades'
  })

  /** As unidades que o foco atual abrange — o que o seletor mostra como escolhido. */
  const unidadesEmFoco = computed<Unidade[]>(() => {
    if (unidadeAtual.value) return [unidadeAtual.value]
    if (empresaAtual.value) return empresaAtual.value.unidades
    return unidades.value
  })

  async function carregar(): Promise<void> {
    const { data } = await client.get<Contexto>('/contexto')
    empresas.value = data.empresas
    empresasInteiras.value = data.empresas_inteiras
    empresaSelecionada.value = data.empresa_selecionada
    unidadeSelecionada.value = data.unidade_selecionada
    carregado.value = true
    aplicarMarca(corAtiva.value)
  }

  /**
   * Muda o foco. Um PUT só, com o par inteiro.
   *
   * Mandar empresa e unidade em chamadas separadas deixaria a tela por um instante
   * num par incoerente — "empresa A, unidade da empresa B" —, e é justamente esse
   * estado que o servidor recusa a gravar.
   */
  async function focar(alvo: {
    empresa?: number | null
    unidade?: number | null
  }): Promise<void> {
    const { data } = await client.put<{
      empresa_selecionada: number | null
      unidade_selecionada: number | null
    }>('/contexto', {
      empresa_id: alvo.empresa ?? null,
      unidade_id: alvo.unidade ?? null,
    })

    // Usa o que o servidor devolveu, não o que foi pedido: se o acesso tiver sido
    // revogado, ele responde null e a tela precisa refletir isso.
    empresaSelecionada.value = data.empresa_selecionada
    unidadeSelecionada.value = data.unidade_selecionada
    aplicarMarca(corAtiva.value)
    versao.value += 1
  }

  const selecionarEmpresa = (empresaId: number | null): Promise<void> =>
    focar({ empresa: empresaId })

  const selecionarUnidade = (unidadeId: number | null): Promise<void> =>
    focar({ unidade: unidadeId })

  const voltarAoConsolidado = (): Promise<void> => focar({})

  /** Atualiza uma empresa já carregada (usado depois de editar a cor na tela). */
  function substituirEmpresa(empresa: Empresa): void {
    const indice = empresas.value.findIndex((e) => e.id === empresa.id)
    if (indice === -1) return

    // Preserva as unidades: o endpoint de atualização devolve a empresa sem elas.
    empresas.value[indice] = { ...empresa, unidades: empresas.value[indice].unidades }
    aplicarMarca(corAtiva.value)
  }

  function limpar(): void {
    empresas.value = []
    empresasInteiras.value = []
    empresaSelecionada.value = null
    unidadeSelecionada.value = null
    carregado.value = false
    aplicarMarca(null)
  }

  return {
    empresas,
    empresasInteiras,
    unidades,
    empresaSelecionada,
    unidadeSelecionada,
    unidadeAtual,
    unidadesEmFoco,
    empresaAtual,
    corAtiva,
    consolidado,
    focoEmEmpresa,
    seletorRelevante,
    rotulo,
    carregado,
    versao,
    carregar,
    focar,
    selecionarEmpresa,
    selecionarUnidade,
    voltarAoConsolidado,
    substituirEmpresa,
    limpar,
  }
})
